/**
 * ==============================================================================
 * BUSINESS LAYER (BL) - TIRO A GOL VERSIÓN 2 (TIEMPO Y ZONAS)
 * Funciones para comunicarse con el nuevo controlador RunToGoalV2Controller.cs
 * ==============================================================================
 */

/**
 * Solicita al servidor la creación de una nueva partida V2 basada en tiempo.
 * @param {Object} matchConfiguration - Objeto con la configuración de la partida.
 * @param {number} matchConfiguration.matchDurationSeconds - Segundos que durará la partida.
 * @param {number} matchConfiguration.tapsPerTick - Cada cuántos taps se enviará la petición (para el celular).
 * @param {number} matchConfiguration.minimumUpdateMilliseconds - Frecuencia de actualización de pantalla.
 * @returns {Promise<Object>} Objeto con el resultado de la operación (success, matchId, message).
 */
const CreateRunToGoalV2Match = async (matchConfiguration) => {
    try {
        const responseFromBackend = await fetch(`${process.env.REACT_APP_API_URL}runtogoalv2/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(matchConfiguration)
        });

        const responseData = await responseFromBackend.json();

        if (!responseFromBackend.ok) {
            return { success: false, message: responseData.message || 'Error al crear la partida V2.' };
        }

        return { success: true, data: responseData };
    } catch (networkError) {
        return { success: false, message: 'Error de red al intentar crear la partida V2.' };
    }
};

/**
 * Da la orden al servidor para iniciar el cronómetro de la partida.
 * @param {string} matchIdentifier - El ID único de la partida creada.
 */
const StartRunToGoalV2Match = async (matchIdentifier) => {
    try {
        const responseFromBackend = await fetch(`${process.env.REACT_APP_API_URL}runtogoalv2/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matchIdentifier: matchIdentifier })
        });

        const responseData = await responseFromBackend.json();
        return { success: responseFromBackend.ok, message: responseData.message };
    } catch (networkError) {
        return { success: false, message: 'Error de red al iniciar la partida V2.' };
    }
};

/**
 * Da la orden al servidor para resetear la partida y apagar el cronómetro.
 * @param {string} matchIdentifier - El ID único de la partida activa.
 */
const ResetRunToGoalV2Match = async (matchIdentifier) => {
    try {
        const responseFromBackend = await fetch(`${process.env.REACT_APP_API_URL}runtogoalv2/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matchIdentifier: matchIdentifier })
        });

        const responseData = await responseFromBackend.json();
        return { success: responseFromBackend.ok, message: responseData.message };
    } catch (networkError) {
        return { success: false, message: 'Error de red al resetear la partida V2.' };
    }
};

/**
 * Función silenciosa que usan los celulares para enviar ráfagas de taps.
 * @param {Object} tapData - Información de los taps a enviar.
 * @param {string} tapData.matchIdentifier - El ID de la partida activa.
 * @param {string} tapData.zoneIdentifier - La zona a la que pertenece el usuario (ej. "norte").
 * @param {number} tapData.tapsObtained - Cantidad de taps realizados.
 */
const AddTapsToRunToGoalV2Zone = async ({ matchIdentifier, zoneIdentifier, tapsObtained }) => {
    try {
        const responseFromBackend = await fetch(`${process.env.REACT_APP_API_URL}runtogoalv2/add-taps`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matchIdentifier, zoneIdentifier, tapsObtained })
        });

        return { success: responseFromBackend.ok };
    } catch (networkError) {
        // Fallo silencioso para no interrumpir al usuario en caso de micro-corte
        return { success: false };
    }
};

/**
 * Da la orden al servidor para terminar el juego anticipadamente y calcular al ganador.
 * @param {string} matchIdentifier - El ID único de la partida activa.
 */
const FinishRunToGoalV2MatchEarly = async (matchIdentifier) => {
    try {
        const responseFromBackend = await fetch(`${process.env.REACT_APP_API_URL}runtogoalv2/finish-early`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matchIdentifier: matchIdentifier })
        });

        const responseData = await responseFromBackend.json();
        return { success: responseFromBackend.ok, message: responseData.message };
    } catch (networkError) {
        return { success: false, message: 'Error de red al terminar la partida anticipadamente.' };
    }
};

/**
 * Dispara la señal para mostrar la portada animada de La Moderna en la pantalla gigante.
 */
const ShowRunToGoalV2FrontPage = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}admin/RunToGoal/ShowFrontPage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        return { success: response.ok };
    } catch (error) {
        return { success: false, message: 'Error de red al mostrar la portada.' };
    }
};

export { CreateRunToGoalV2Match, StartRunToGoalV2Match, ResetRunToGoalV2Match, AddTapsToRunToGoalV2Zone, FinishRunToGoalV2MatchEarly, ShowRunToGoalV2FrontPage };